import { useEffect, useMemo, useSyncExternalStore, type JSX } from "react";
import { useThree } from "@react-three/fiber";
import { attachPass } from "@faro-lotv/app-component-toolbox";
import type { LodPointCloud } from "@faro-lotv/lotv";
import { Camera, Matrix4, Uniform } from "three";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import { isUnknownObject } from "@/object-cache-type-guard";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { SurfacePointFilter } from "../surface-wand/surface-point-filter";
import {
  getCadAnalysisReference,
  subscribeCadAnalysisReferences,
} from "./cad-analysis-runtime";
import {
  CAD_DISTANCE_SHADER,
  CAD_SCOPE_SHADER,
  type CadDistanceReference,
} from "./cad-distance-reference";

const predicate = `
uniform mat4 uOpacityWorldToCloud;
uniform float uOpacitySearchDistance;
bool selectedSurfacePoint(vec4 world) {
  #ifdef USE_DISTANCE_COLORMAP
  vec3 point = (uOpacityWorldToCloud * world).xyz;
  float deviation = 0.0;
  return !analysisInScope(point) || analysisDistance(point, uOpacitySearchDistance, deviation);
  #else
  return true;
  #endif
}`;

/** Post-shading unmatched opacity with pre-depth rejection only at zero opacity. */
export class CadUnmatchedPointFilter extends SurfacePointFilter {
  private readonly range: Uniform<number>;

  /**
   * @param camera Camera owning the point-cloud pass.
   * @param reference Ready reference borrowed from the analysis renderer.
   * @param cloudWorld Frozen cloud-to-world transform.
   */
  constructor(
    camera: Camera,
    reference: CadDistanceReference,
    cloudWorld: Matrix4,
  ) {
    const range = new Uniform(0);
    super(camera, {
      shader: `#define USE_DISTANCE_COLORMAP\n${CAD_DISTANCE_SHADER}\n${CAD_SCOPE_SHADER}\n${predicate}`,
      vertexShader: `#ifdef USE_DISTANCE_COLORMAP\nbool analysisInScope(vec3 position);\nbool analysisDistance(vec3 position, float range, out float deviation);\n#endif\n${predicate}`,
      uniforms: {
        ...reference.uniforms,
        uOpacitySearchDistance: range,
        uOpacityWorldToCloud: new Uniform(cloudWorld.clone().invert()),
      },
    });
    this.range = range;
  }

  /** @param distance Search distance used by the colour classification, in metres. */
  setSearchDistance(distance: number): void {
    this.range.value = distance;
  }
}

/** @returns An opacity pass only for the active, ready CAD comparison and its source cloud. */
export function CadUnmatchedOpacityEffect({
  pointCloud,
}: {
  pointCloud: LodPointCloud | null;
}): JSX.Element | null {
  const analysis = useAppSelector(selectActiveAnalysis);
  const status = useAppSelector((state) =>
    analysis
      ? state.pointCloudAnalysisTool.cadStatus?.[analysis.id]
      : undefined,
  );
  const reference = useSyncExternalStore(subscribeCadAnalysisReferences, () =>
    pointCloud && isUnknownObject(pointCloud) && analysis?.kind === "cad"
      ? getCadAnalysisReference(pointCloud, analysis.id)
      : undefined,
  );
  if (
    !pointCloud ||
    !isUnknownObject(pointCloud) ||
    analysis?.kind !== "cad" ||
    !analysis.isVisible ||
    pointCloud.iElement.id !== analysis.parentId ||
    status?.phase !== "ready"
  ) {
    return null;
  }
  const opacity = analysis.deviationColouring?.unmatchedOpacity ?? 1;
  if (!reference || opacity >= 1) return null;
  return (
    <ActiveOpacity
      pointCloud={pointCloud}
      reference={reference}
      cloudTransform={analysis.cadReference.cloudTransform}
      opacity={opacity}
      range={analysis.searchDistance}
    />
  );
}

function ActiveOpacity({
  pointCloud,
  reference,
  cloudTransform,
  opacity,
  range,
}: {
  pointCloud: LodPointCloud;
  reference: CadDistanceReference;
  cloudTransform: number[];
  opacity: number;
  range: number;
}): JSX.Element {
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);
  const events = useSceneEvents();
  const filter = useMemo(
    () =>
      new CadUnmatchedPointFilter(
        camera,
        reference,
        new Matrix4().fromArray(cloudTransform),
      ),
    [camera, reference, cloudTransform],
  );
  useEffect(() => {
    filter.setSearchDistance(range);
    filter.setOpacity(opacity);
    events.invalidatePointCloudScene.emit();
    invalidate();
  }, [filter, range, opacity, events, invalidate]);
  useEffect(() => {
    const restore = filter.attach(pointCloud.material);
    return () => {
      restore();
      filter.dispose();
      events.invalidatePointCloudScene.emit();
      invalidate();
    };
  }, [filter, pointCloud, events, invalidate]);
  return <primitive object={filter} attach={attachPass} />;
}
